/** @jsxImportSource @emotion/react */

import type { ReactElement, ReactNode, Ref } from 'react';
import { useId } from 'react';
import { cx } from '@emotion/css';
import ODSGroup from '../ODSGroup';
import ODSFormFeedback from '../ODSFormFeedback';

interface Props extends Omit<React.ComponentProps<'input'>, 'size' | 'prefix' | 'type'> {
  /** The text that appears after switch */
  label?: ReactNode;
  /** The text that appears before the switch */
  preLabel?: ReactNode;
  /** Tell the user a little more about what this does */
  helpText?: ReactNode;
  /** If invalid should an error message */
  errorMessage?: ReactNode;
  /** On success show this text */
  successMessage?: ReactNode;
  /** Ignore */
  type?: 'radio' | 'checkbox' | 'switch';
  /** Mark it valid */
  valid?: boolean;
  /** Mark it invalid */
  invalid?: boolean;
  /** For use with ODSGrid, how many columns should the control be, all of them by default */
  columns?: number;
  /** Should the error display as a tooltip? */
  tooltip?: boolean;
  /** Label too big, make it small */
  smallLabel?: boolean;
  /** Need it to be a fancy color? */
  color?: 'primary' | 'danger' | 'focus' | 'synced' | 'black' | 'secondary';
  /** Need it to be larger, pass in a size */
  size?: 'sm' | 'md' | 'lg';
  /** Add if you need it to be displayed inline */
  inline?: boolean;
  /** Pass in the testid for the switch */
  testSelector?: string;
  /** Ref of checkbox input */
  innerRef?: Ref<HTMLInputElement>;
}

function ODSSwitch({
  label,
  preLabel,
  invalid,
  valid,
  helpText,
  errorMessage,
  successMessage,
  columns,
  type = 'checkbox',
  name,
  id,
  tooltip,
  required,
  smallLabel,
  color = 'primary',
  size = 'sm',
  inline,
  defaultChecked,
  checked,
  testSelector = `ods-${type}`,
  className,
  disabled,
  'aria-describedby': ariaDescribedBy,
  innerRef,
  ...componentProps
}: Props): ReactElement {
  const uid = useId();

  let dimension = 16;
  switch (size) {
    case 'md':
      dimension = 18;
      break;
    case 'lg':
      dimension = 20;
      break;
    default:
  }
  const inputID = id ?? uid;
  const testId = componentProps['data-testid'] ?? testSelector;

  const helpTextId = helpText ? `${inputID}-help` : undefined;
  const errorMessageId = invalid && errorMessage ? `${inputID}-error` : undefined;
  const successMessageId = valid && successMessage ? `${inputID}-success` : undefined;
  const describedBy =
    [ariaDescribedBy, helpTextId, errorMessageId, successMessageId].filter(Boolean).join(' ') ??
    undefined;

  return (
    <ODSGroup
      {...(columns && { columns })}
      stacked
      inline={inline}
      spacing="sm"
      className={cx('align-items-start position-relative', className, {
        'w-100': !inline,
        'disabled pe-none': disabled,
      })}
      testSelector={`${testId}-control`}
    >
      <label
        className={cx('hstack', { small: smallLabel })}
        css={{
          cursor: 'pointer',
          span: {
            opacity: 0.85,
          },
          '&:has(input:checked) span': {
            opacity: 1,
          },
          '.disabled & span, .disabled &:has(input:checked) span': {
            opacity: 0.4,
          },
        }}
        htmlFor={inputID}
      >
        {preLabel && <span className="user-select-none pe-2">{preLabel}</span>}
        <input
          ref={innerRef}
          type={type === 'switch' ? 'checkbox' : type}
          name={name}
          id={inputID}
          required={required}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className={cx(
            'form-check-input m-0 flex-shrink-0',
            `${type}-${color.replace(/[A-Z]/g, '-$&').toLowerCase()}`,
            {
              'is-invalid': invalid,
              'is-valid': valid && !invalid,
            }
          )}
          css={{
            cursor: 'pointer',
            ...(type !== 'switch'
              ? {
                  width: `calc(var(--px-unit) * ${dimension})`,
                  height: `calc(var(--px-unit) * ${dimension})`,
                }
              : {
                  width: `calc(var(--px-unit) * ${dimension} * 1.75) !important`,
                  height: `calc(var(--px-unit) * ${dimension})`,
                }),
          }}
          defaultChecked={defaultChecked}
          checked={checked}
          disabled={disabled}
          data-testid={testId}
          {...componentProps}
        />
        {label && <span className="user-select-none ps-2">{label}</span>}
      </label>
      {!invalid && valid && successMessage && (
        <ODSFormFeedback id={successMessageId} show valid tooltip={tooltip}>
          {successMessage}
        </ODSFormFeedback>
      )}
      {invalid && errorMessage && (
        <ODSFormFeedback id={errorMessageId} show valid={false} tooltip={tooltip}>
          {errorMessage}
        </ODSFormFeedback>
      )}
      {helpText && (
        <div className="text-secondary small user-select-none" id={helpTextId}>
          {helpText}
        </div>
      )}
    </ODSGroup>
  );
}

export default ODSSwitch;
